import { createContext, useContext, useCallback } from 'react'
import useApiHelpers from 'lib/effects/api-helpers'
import { useState } from 'react'
import axiosRequest from 'lib/axios/api-request'

import type {
  DocumentWithAttributes,
  Document,
  File,
  FileCreate,
} from 'lib/models/document'

import type { UploadFile } from './types'
import { toast } from 'react-toastify'

type DocumentsContextType = {
  documents: DocumentWithAttributes[]
  fetchDocuments: ({
    queryParams,
  }: {
    queryParams?: Record<string, number | string | boolean>
  }) => Promise<void>
  isFetchingDocuments: boolean
  createDocument: (
    document: Partial<Document>
  ) => Promise<DocumentWithAttributes>
  updateDocument: (
    id: number,
    document: Partial<Document>
  ) => Promise<DocumentWithAttributes>
  deleteDocument: (documentId: number) => Promise<void>
  isDeletingDocument: boolean
  saveDocument: (documentId: number) => Promise<DocumentWithAttributes>
  createFile: (file: FileCreate) => Promise<void>
  updateFile: (id: number, file: Partial<File>) => Promise<void>
  attachFiles: (documentId: number, files: UploadFile[]) => Promise<File[]>
  isUploading: boolean
  publishedDocuments: DocumentWithAttributes[]
}

const DocumentsContext = createContext<DocumentsContextType>(
  {} as DocumentsContextType
)

const useDocumentsContext = () => {
  const context = useContext(DocumentsContext)
  if (context === undefined) {
    throw new Error(
      'useDocumentsContext must be used within an DocumentsProvider'
    )
  }
  return context
}

const DocumentsProvider = ({ children }: { children: React.ReactNode }) => {
  const {
    items: documents,
    fetchItems: fetchDocuments,
    isFetching: isFetchingDocuments,
    createItem: createDocument,
    updateItem: updateDocument,
    deleteItem: deleteDocument,
    isDeleting: isDeletingDocument,
  } = useApiHelpers({
    baseUrl: '/api/documents',
    type: 'documents',
  })

  const { createItem: createFile, updateItem: updateFile } = useApiHelpers({
    baseUrl: '/api/files',
    type: 'files',
  })

  const [isUploading, setIsUploading] = useState(false)

  const saveDocument = useCallback(
    async (documentId: number) => {
      await updateDocument(documentId, { status: 'published' })
      toast('Document saved to case.', {
        toastId: 'document-saved-to-case',
      })
    },
    [updateDocument]
  )

  const attachFiles = useCallback(
    async (documentId: number, files: File[]) => {
      try {
        setIsUploading(true)

        return await Promise.all(
          files.map(async (file: File, orderIndex: number) => {
            const createdFile = await createFile({
              source: 'upload',
              type: file.type,
              documentId,
              orderIndex,
            })

            await axiosRequest({
              url: createdFile.attributes.url,
              method: 'PUT',
              data: file,
              headers: { 'Content-Type': file.type },
              sendToken: false,
            })

            return createdFile
          })
        )
      } finally {
        setIsUploading(false)
      }
    },
    [setIsUploading, createFile]
  )

  const values = {
    documents,
    fetchDocuments,
    isFetchingDocuments,
    createDocument,
    updateDocument,
    deleteDocument,
    isDeletingDocument,
    saveDocument,
    createFile,
    attachFiles,
    isUploading,
    updateFile,
  }

  return (
    <DocumentsContext.Provider
      value={values as unknown as DocumentsContextType}
    >
      {children}
    </DocumentsContext.Provider>
  )
}

export { DocumentsContext, DocumentsProvider, useDocumentsContext }
